import { Card, Row, Col, Typography, Switch, Select, Button, Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getNotificationSettings,
  updateNotificationSettings,
} from "../../actionCreator/calendarActionCreator";
import { useAlert } from "../../context/AlertProvider";
import { useEffect, useState } from "react";
import { isEqual } from "lodash";

const { Text } = Typography;

const reminderOptions = [
  { label: "5 minutes before", value: 5 },
  { label: "10 minutes before", value: 10 },
  { label: "15 minutes before", value: 15 },
  { label: "30 minutes before", value: 30 },
  { label: "1 hour before", value: 60 },
];

const NotificationSettings = () => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const [form] = Form.useForm();

  const notificationSettings = useSelector(
    (state) => state.calendarReducer.notificationSettings
  );

  const [initialValues, setInitialValues] = useState(null);
  const [isChanged, setIsChanged] = useState(false);

  // 🔹 Fetch current settings on mount
  useEffect(() => {
    dispatch(getNotificationSettings());
  }, [dispatch]);

  // 🔹 Set form values once data is available
  useEffect(() => {
    if (notificationSettings) {
      const formatted = {
        emailReminder: notificationSettings.emailReminder ?? true,
        reminderMinutesBefore: notificationSettings.reminderMinutesBefore ?? 15,
      };

      form.setFieldsValue(formatted);
      setInitialValues(formatted);
      setIsChanged(false); // reset when reloaded
    }
  }, [notificationSettings, form]);

  // 🔹 Detect form changes
  const handleValuesChange = (_, allValues) => {
    if (!initialValues) return;
    setIsChanged(!isEqual(allValues, initialValues));
  };

  // 🔹 Save handler
  const handleSaveChange = async (values) => {
    const res = await dispatch(
      updateNotificationSettings({
        emailReminder: values.emailReminder,
        reminderMinutesBefore: values.reminderMinutesBefore,
      })
    );

    if (res?.status === 200) {
      showAlert("success", "Notification settings updated successfully.");
      dispatch(getNotificationSettings());
    } else {
      showAlert("error", "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="tw-mt-6">
      <div className="tw-mt-4 tw-mb-6">
        <h2 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-1">Notification Settings</h2>
        <p className="tw-text-sm tw-text-slate-500">
          Configure reminders and follow-up notifications.
        </p>
      </div>

      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card
            title={
              <div className="tw-py-2">
                <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-mb-1">Reminder Settings</h3>
                <p className="tw-text-xs tw-text-slate-400 tw-font-normal tw-mb-0">
                  Set up automatic reminders for meetings.
                </p>
              </div>
            }
            className="!tw-rounded-2xl !tw-border-slate-100 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]"
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveChange}
              onValuesChange={handleValuesChange}
              initialValues={{
                emailReminder: true,
                reminderMinutesBefore: 15,
              }}
            >
              <Form.Item
                label={
                  <span className="tw-text-sm tw-font-bold tw-text-slate-700">
                    Email Reminders
                  </span>
                }
                name="emailReminder"
                valuePropName="checked"
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-rounded-2xl tw-bg-slate-50/50 tw-border tw-border-solid tw-border-slate-100/80">
                  <span className="tw-text-xs tw-text-slate-500">Send email reminders to participants before meeting</span>
                  <Switch />
                </div>
              </Form.Item>

              <Form.Item
                label={
                  <span className="tw-text-sm tw-font-bold tw-text-slate-700">
                    Reminder Timing
                  </span>
                }
                name="reminderMinutesBefore"
              >
                <Select
                  options={reminderOptions}
                  placeholder="Select reminder time"
                  size="large"
                  className="!tw-h-[44px] !tw-rounded-xl"
                />
              </Form.Item>

              <div className="tw-flex tw-justify-end tw-mt-8">
                <Button
                  type="primary"
                  className="theme-btn tw-h-[42px] !tw-rounded-xl tw-font-bold tw-text-sm tw-shadow-[0_8px_20px_rgba(93,68,255,0.25)] hover:tw-shadow-[0_12px_24px_rgba(93,68,255,0.35)] hover:tw-scale-[1.01] tw-transition-all tw-duration-300"
                  htmlType="submit"
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NotificationSettings;
